import { useState, useEffect, useCallback } from 'react';
import { 
  RefreshCw, Clock, CheckCircle2, XCircle, AlertCircle, 
  ArrowDownLeft, ArrowUpRight, History, Activity,
  Wallet, DollarSign, Hash, Banknote, ArrowRightLeft,
  Timer, CircleDollarSign, Network, TrendingUp, TrendingDown
} from 'lucide-react';
import { walletAPI } from '../../services/api';

import { fmtFixed } from '../../utils/format';
const WalletActivityView = ({ type = 'active' }) => {
  const [activeDeposits, setActiveDeposits] = useState([]);
  const [activeWithdrawals, setActiveWithdrawals] = useState([]);
  const [depositHistory, setDepositHistory] = useState([]);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [transactionStats, setTransactionStats] = useState({ deposits: 0, withdrawals: 0 });
  const [loading, setLoading] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('all');

  // Fetch active deposits and withdrawals
  const fetchActiveData = useCallback(async () => {
    setLoading(true);
    try {
      const [deposits, withdrawals] = await Promise.all([
        walletAPI.getActiveDeposits().catch(() => []),
        walletAPI.getActiveWithdrawals().catch(() => [])
      ]);
      setActiveDeposits(deposits || []);
      setActiveWithdrawals(withdrawals || []);
    } catch (error) {
      console.error('Error fetching active data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch history - now fetches from multiple sources
  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all transaction history from the combined endpoint
      const allHistoryData = await walletAPI.getAllTransactionHistory(100).catch(() => ({ transactions: [], deposits_count: 0, withdrawals_count: 0 }));
      
      // Also fetch individual histories for the sub-tabs
      const [deposits, withdrawals] = await Promise.all([
        walletAPI.getDepositHistory(50).catch(() => []),
        walletAPI.getWithdrawalHistory(50).catch(() => [])
      ]);
      
      setDepositHistory(deposits || []);
      setWithdrawalHistory(withdrawals || []);
      setAllTransactions(allHistoryData.transactions || []);
      setTransactionStats({
        deposits: allHistoryData.deposits_count || deposits?.length || 0,
        withdrawals: allHistoryData.withdrawals_count || withdrawals?.length || 0
      });
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (type === 'active') {
      fetchActiveData();
    } else {
      fetchHistory();
    }
  }, [type, fetchActiveData, fetchHistory]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format amount with crypto symbol
  const formatAmount = (amount, cryptoType) => {
    if (!amount && amount !== 0) return '0';
    const num = parseFloat(amount);
    
    // Format based on crypto type
    if (cryptoType === 'USDT' || cryptoType === 'USD') {
      return `${fmtFixed(num, 2)} ${cryptoType || 'USDT'}`;
    }
    if (num < 0.0001) {
      return `${fmtFixed(num, 8)} ${cryptoType || 'CRYPTO'}`;
    }
    if (num < 1) {
      return `${fmtFixed(num, 6)} ${cryptoType || 'CRYPTO'}`;
    }
    return `${fmtFixed(num, 4)} ${cryptoType || 'CRYPTO'}`;
  };

  // Status badge component
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock, text: 'Pending' },
      approved: { bg: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle2, text: 'Approved' },
      completed: { bg: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle2, text: 'Completed' },
      paid: { bg: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle2, text: 'Paid' },
      rejected: { bg: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle, text: 'Rejected' },
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config.bg}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.text}
      </span>
    );
  };

  // Active items combined
  const activeItems = [
    ...activeDeposits.map(d => ({ ...d, itemType: 'deposit' })),
    ...activeWithdrawals.map(w => ({ ...w, itemType: 'withdrawal' }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Get filtered items based on sub-tab
  const getFilteredItems = () => {
    if (type === 'active') {
      if (activeSubTab === 'all') return activeItems;
      if (activeSubTab === 'deposits') return activeDeposits.map(d => ({ ...d, itemType: 'deposit' }));
      if (activeSubTab === 'withdrawals') return activeWithdrawals.map(w => ({ ...w, itemType: 'withdrawal' }));
    } else {
      if (activeSubTab === 'all') return allTransactions;
      if (activeSubTab === 'deposits') return depositHistory;
      if (activeSubTab === 'withdrawals') return withdrawalHistory;
    }
    return [];
  };

  const filteredItems = getFilteredItems();

  // Render active view
  if (type === 'active') {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Activity className="h-5 w-5 text-blue-400" />
            <h3 className="text-lg font-bold">Active Transactions</h3>
          </div>
          <button
            onClick={fetchActiveData}
            disabled={loading}
            className="p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 text-blue-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <p className="text-sm text-gray-400">Pending deposits and withdrawals</p>

        {/* Sub tabs */}
        <div className="flex space-x-2">
          {['all', 'deposits', 'withdrawals'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveSubTab(tab)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeSubTab === tab
                  ? 'bg-blue-500/30 text-blue-300'
                  : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50'
              }`}
            >
              {tab === 'all' ? 'All' : tab === 'deposits' ? 'Deposits' : 'Withdrawals'}
            </button>
          ))}
        </div>

        {/* Items list */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-400" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="h-12 w-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 font-medium">No Active Transactions</p>
            <p className="text-gray-500 text-sm">Your pending transactions will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredItems.map((item, index) => (
              <div
                key={item.id || index}
                className="p-4 bg-gray-900/50 rounded-xl border border-gray-800/50 hover:border-blue-500/30 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2.5 rounded-lg ${
                      item.itemType === 'deposit' 
                        ? 'bg-green-500/20' 
                        : 'bg-orange-500/20'
                    }`}>
                      {item.itemType === 'deposit' ? (
                        <ArrowDownLeft className="h-5 w-5 text-green-400" />
                      ) : (
                        <ArrowUpRight className="h-5 w-5 text-orange-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium capitalize text-white">
                        {item.itemType}
                      </p>
                      <p className="text-xs text-gray-400">
                        {item.crypto_type} • {item.network}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(item.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${item.itemType === 'deposit' ? 'text-green-400' : 'text-orange-400'}`}>
                      {item.itemType === 'deposit' ? '+' : '-'}{formatAmount(item.amount, item.crypto_type)}
                    </p>
                    {getStatusBadge(item.status)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Render history view
  return (
    <div className="space-y-4">
      {/* Header with stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <History className="h-5 w-5 text-pink-400" />
          <h3 className="text-lg font-bold">Transaction History</h3>
        </div>
        <button
          onClick={fetchHistory}
          disabled={loading}
          className="p-2 rounded-lg bg-pink-500/20 hover:bg-pink-500/30 transition-colors"
        >
          <RefreshCw className={`h-4 w-4 text-pink-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 bg-green-500/10 rounded-xl border border-green-500/20">
          <div className="flex items-center space-x-2">
            <TrendingUp className="h-4 w-4 text-green-400" />
            <span className="text-xs text-gray-400">Deposits</span>
          </div>
          <p className="text-lg font-bold text-green-400 mt-1">{transactionStats.deposits}</p>
        </div>
        <div className="p-3 bg-orange-500/10 rounded-xl border border-orange-500/20">
          <div className="flex items-center space-x-2">
            <TrendingDown className="h-4 w-4 text-orange-400" />
            <span className="text-xs text-gray-400">Withdrawals</span>
          </div>
          <p className="text-lg font-bold text-orange-400 mt-1">{transactionStats.withdrawals}</p>
        </div>
      </div>

      <p className="text-sm text-gray-400">All completed deposits and withdrawals</p>

      {/* Sub tabs */}
      <div className="flex space-x-2">
        {['all', 'deposits', 'withdrawals'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveSubTab(tab)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeSubTab === tab
                ? tab === 'deposits' ? 'bg-green-500/30 text-green-300' 
                  : tab === 'withdrawals' ? 'bg-orange-500/30 text-orange-300'
                  : 'bg-pink-500/30 text-pink-300'
                : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50'
            }`}
          >
            {tab === 'all' ? `All (${allTransactions.length})` 
              : tab === 'deposits' ? `Deposits (${depositHistory.length})` 
              : `Withdrawals (${withdrawalHistory.length})`}
          </button>
        ))}
      </div>

      {/* Items list */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-pink-400" />
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-8">
          <History className="h-12 w-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 font-medium">No Transaction History</p>
          <p className="text-gray-500 text-sm">Your completed transactions will appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredItems.map((item, index) => {
            const itemType = item.trade_type || item.type || (item.transaction_hash ? 'deposit' : 'withdrawal');
            const isDeposit = itemType === 'deposit';
            
            return (
              <div
                key={item.id || index}
                className="p-4 bg-gray-900/50 rounded-xl border border-gray-800/50 hover:border-pink-500/30 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2.5 rounded-lg ${
                      isDeposit 
                        ? 'bg-green-500/20' 
                        : 'bg-orange-500/20'
                    }`}>
                      {isDeposit ? (
                        <ArrowDownLeft className="h-5 w-5 text-green-400" />
                      ) : (
                        <ArrowUpRight className="h-5 w-5 text-orange-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium capitalize text-white">
                        {isDeposit ? 'Deposit' : 'Withdrawal'}
                      </p>
                      <p className="text-xs text-gray-400">
                        {item.crypto_type} • {item.network}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(item.completed_at || item.recorded_at || item.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${isDeposit ? 'text-green-400' : 'text-orange-400'}`}>
                      {isDeposit ? '+' : '-'}{formatAmount(item.amount, item.crypto_type)}
                    </p>
                    {getStatusBadge(item.status)}
                  </div>
                </div>
                
                {/* Balance change info */}
                {(item.balance_before !== undefined || item.balance_after !== undefined) && (
                  <div className="mt-2 pt-2 border-t border-gray-800/50">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Balance Change:</span>
                      <span className="text-gray-400">
                        {item.balance_before !== undefined ? formatAmount(item.balance_before, item.crypto_type) : '—'} 
                        {' → '} 
                        {item.balance_after !== undefined ? formatAmount(item.balance_after, item.crypto_type) : '—'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WalletActivityView;
